import { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { getAutocompleteSuggestions } from '../../../services/UnlimitedService';
import { Mixpanel } from '../../../services/Mixpanel';
import { useLandingPageVariant } from '../../../contexts/LandingPageVariantContext';
import { STATIC_PILL_SUGGESTIONS } from '../constants';

/**
 * Provides debounced autocomplete suggestions and helper-pill suggestions for the Unlimited search input.
 *
 * @param {Object} params
 * @param {string} params.query - Current query text (owned by parent).
 * @param {Function} params.setQuery - Setter for query text (owned by parent).
 * @param {Object} params.suggestionContext - Contextual data sent to the autocomplete API.
 * @param {boolean} params.isSignedIn - Whether the current user is authenticated.
 * @param {Object} params.inputRef - React ref for the search textarea element.
 * @param {Function} params.adjustTextareaHeight - Callback to resize the textarea after content changes.
 * @param {Function} params.setError - Setter to display error messages.
 * @param {Function} params.onSubmit - Callback invoked when the user presses Enter without a selected suggestion.
 * @returns {{
 *   suggestions: Object[],
 *   selectedIndex: number,
 *   activePill: string|null,
 *   libraryPushedDown: boolean,
 *   isPillLoading: boolean,
 *   displayedSuggestions: Object[],
 *   fetchSuggestions: Function,
 *   handleInputChange: Function,
 *   handleKeyDown: Function,
 *   handlePillClick: Function,
 *   selectSuggestion: Function,
 *   highlightMatch: Function,
 *   clearPillCache: Function,
 * }}
 */
export const useAutocomplete = ({
  query,
  setQuery,
  suggestionContext,
  isSignedIn,
  inputRef,
  adjustTextareaHeight,
  setError,
  onSubmit,
}) => {
  const landingPageVariant = useLandingPageVariant();
  const [suggestions, setSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // Helper pills state
  const [activePill, setActivePill] = useState(null);
  const [pillSuggestions, setPillSuggestions] = useState({});
  const [libraryPushedDown, setLibraryPushedDown] = useState(false);

  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  const isPillLoading = Boolean(activePill) && pillSuggestions[activePill] === undefined;

  // Latch library down on pill click or when suggestions arrive
  useEffect(() => {
    if (activePill || (query.trim() && suggestions.length > 0)) {
      setLibraryPushedDown(true);
    }
  }, [activePill, query, suggestions.length]);

  // Debounced fetch for autocomplete suggestions
  const fetchSuggestions = useCallback(
    async (searchQuery) => {
      if (!searchQuery.trim() || searchQuery.length < 2 || searchQuery.length > 150) {
        setSuggestions([]);
        return;
      }

      const currentRequestId = ++requestIdRef.current;

      try {
        const result = await getAutocompleteSuggestions({
          query: searchQuery,
          context: suggestionContext,
        });

        if (currentRequestId === requestIdRef.current) {
          setSuggestions(result.suggestions || []);
        }
      } catch (error) {
        if (currentRequestId === requestIdRef.current) {
          console.error('Error fetching suggestions:', error);
          setSuggestions([]);
        }
      }
    },
    [suggestionContext]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setSelectedIndex(-1);
    setError(null);
    setSuggestions([]);
    setActivePill(null);
    if (!value.trim()) setLibraryPushedDown(false);

    adjustTextareaHeight();

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
  };

  const displayedSuggestions = query.trim()
    ? suggestions
    : activePill && pillSuggestions[activePill]
      ? pillSuggestions[activePill]
      : [];

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    const maxIndex = Math.min(displayedSuggestions.length, 4) - 1;

    switch (e.key) {
      case 'ArrowDown':
        if (displayedSuggestions.length > 0) {
          e.preventDefault();
          setSelectedIndex((prev) => (prev < maxIndex ? prev + 1 : prev));
        }
        break;
      case 'ArrowUp':
        if (displayedSuggestions.length > 0) {
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        }
        break;
      case 'Enter':
        if (e.shiftKey) {
          return;
        }
        e.preventDefault();
        if (selectedIndex >= 0 && displayedSuggestions[selectedIndex]) {
          selectSuggestion(displayedSuggestions[selectedIndex]);
        } else if (query.trim()) {
          onSubmit();
        }
        break;
      case 'Escape':
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  // Handle helper pill click
  const handlePillClick = async (pill) => {
    if (activePill === pill.id) {
      setActivePill(null);
      if (!query.trim()) setLibraryPushedDown(false);
      return;
    }

    setActivePill(pill.id);
    Mixpanel.track('Unlimited Helper Pill Selected', {
      pill_id: pill.id,
      pill_label: pill.label,
    });

    if (pillSuggestions[pill.id] !== undefined) return;

    // Variant pill ids aren't in STATIC_PILL_SUGGESTIONS, so signed-out users
    // hit the API for them (it allows anonymous callers, rate-limited by IP).
    // A variant without pills keeps the static path for profile-derived pills.
    if (!isSignedIn && !landingPageVariant?.pills?.length) {
      const statics = STATIC_PILL_SUGGESTIONS[pill.id] || [];
      setPillSuggestions((prev) => ({ ...prev, [pill.id]: statics }));
      return;
    }

    try {
      const result = await getAutocompleteSuggestions({
        query: pill.label,
        context: suggestionContext,
        source: 'pill',
      });
      setPillSuggestions((prev) => ({ ...prev, [pill.id]: result.suggestions || [] }));
    } catch (err) {
      console.error('Error fetching pill suggestions:', err);
      setPillSuggestions((prev) => ({ ...prev, [pill.id]: [] }));
    }
  };

  const selectSuggestion = (suggestion) => {
    const fromPill = activePill;
    Mixpanel.track('Unlimited Suggestion Selected', {
      suggestion_text: suggestion.text,
      had_query: query.trim().length > 0,
      ...(fromPill && { from_pill: fromPill }),
    });
    setQuery(suggestion.text);
    setSelectedIndex(-1);
    setActivePill(null);
    inputRef.current?.focus();
    fetchSuggestions(suggestion.text);
  };

  const highlightMatch = (text, searchQuery) => {
    if (!text || !searchQuery?.trim()) return text;

    const lowerText = text?.toLowerCase();
    const lowerQuery = searchQuery?.toLowerCase();
    const index = lowerText?.indexOf(lowerQuery);

    if (index === -1 || !lowerText) return text;

    return (
      <>
        {text.slice(0, index)}
        <span className='text-gray-500 font-light'>
          {text.slice(index, index + searchQuery.length)}
        </span>
        {text.slice(index + searchQuery.length)}
      </>
    );
  };

  // Clear cached pill suggestions (call when context changes)
  const clearPillCache = useCallback(() => {
    setPillSuggestions({});
    setActivePill(null);
  }, []);

  return useMemo(
    () => ({
      suggestions,
      setSuggestions,
      selectedIndex,
      setSelectedIndex,
      activePill,
      libraryPushedDown,
      isPillLoading,
      displayedSuggestions,
      fetchSuggestions,
      handleInputChange,
      handleKeyDown,
      handlePillClick,
      selectSuggestion,
      highlightMatch,
      clearPillCache,
    }),
    [
      suggestions,
      selectedIndex,
      activePill,
      libraryPushedDown,
      isPillLoading,
      displayedSuggestions,
      fetchSuggestions,
      query,
      onSubmit,
      inputRef,
      adjustTextareaHeight,
      setQuery,
      setError,
      pillSuggestions,
      isSignedIn,
      suggestionContext,
    ]
  );
};
